import React, { useEffect, useRef } from 'react';
import './horizontalScrollableList.scss';

export type ListItem = {
  id: string | number;
  label: string;
};

type HorizontalScrollableListProps = {
  items: ListItem[];
  selectedId?: string | number;
  onSelect: (id: string | number) => void;
};

const HorizontalScrollableList: React.FC<HorizontalScrollableListProps> = ({
  items,
  selectedId,
  onSelect,
}) => {
  const itemRefs = useRef<Map<string | number, HTMLButtonElement | null>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);
  const dragStartX = useRef<number | null>(null);
  const dragStartScroll = useRef<number>(0);
  const didDrag = useRef(false);

  useEffect(() => {
    const selectedItem = itemRefs.current.get(selectedId!);
    if (selectedItem) {
      selectedItem.scrollIntoView({
        behavior: 'smooth',
        inline: 'center',
        block: 'nearest',
      });
    }
  }, [selectedId]);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (dragStartX.current === null || !containerRef.current) return;
      const delta = dragStartX.current - e.clientX;
      if (Math.abs(delta) > 5) didDrag.current = true;
      containerRef.current.scrollLeft = dragStartScroll.current + delta;
    };

    const handleMouseUp = () => {
      dragStartX.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    dragStartX.current = e.clientX;
    dragStartScroll.current = containerRef.current?.scrollLeft ?? 0;
    didDrag.current = false;
  };

  return (
    <div
      ref={containerRef}
      className="horizontal-scrollable-list"
      onMouseDown={handleMouseDown}
    >
      <div className="list-container">
        {items.map((item) => (
          <button
            key={item.id}
            ref={(el) => {
              itemRefs.current.set(item.id, el);
              return;
            }}
            className={`list-item ${selectedId === item.id ? 'selected' : ''}`}
            onClick={() => {
              if (!didDrag.current) onSelect(item.id);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
    </div>
  );
};

export default HorizontalScrollableList;
